// NOMBASE - Compare Context
// Manages product comparison selection (max 4 products)
import React, { createContext, useContext, useState, useCallback } from 'react';

const CompareContext = createContext(null);
const MAX_COMPARE = 4;

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) throw new Error('useCompare must be used within CompareProvider');
  return context;
};

export const CompareProvider = ({ children }) => {
  const [compareItems, setCompareItems] = useState([]);

  const addToCompare = useCallback((product) => {
    setCompareItems(prev => {
      if (prev.length >= MAX_COMPARE) return prev;
      if (prev.some(p => p.id === product.id)) return prev;
      return [...prev, product];
    });
  }, []);

  const removeFromCompare = useCallback((productId) => {
    setCompareItems(prev => prev.filter(p => p.id !== productId));
  }, []);

  const isInCompare = useCallback((productId) => {
    return compareItems.some(p => p.id === productId);
  }, [compareItems]);

  const toggleCompare = useCallback((product) => {
    setCompareItems(prev => {
      if (prev.some(p => p.id === product.id)) {
        return prev.filter(p => p.id !== product.id);
      }
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, product];
    });
  }, []);

  const clearCompare = useCallback(() => {
    setCompareItems([]);
  }, []);

  const value = {
    compareItems,
    addToCompare,
    removeFromCompare,
    isInCompare,
    toggleCompare,
    clearCompare,
    compareCount: compareItems.length,
    maxCompare: MAX_COMPARE,
    canAddMore: compareItems.length < MAX_COMPARE,
  };

  return (
    <CompareContext.Provider value={value}>
      {children}
    </CompareContext.Provider>
  );
};
